import {
    CodeBlock,
    FeatureType,
    FlowEdge,
    SettingType,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import CustomFeatureBase from '@appblocks/codegeneration/Features/CustomFeatureBase';
import Libraries from '@appblocks/codegeneration/Libraries';
import Nodes from '@appblocks/codegeneration/Nodes';
import Peripherals from '@appblocks/codegeneration/Peripherals';
import Features from '@appblocks/codegeneration/Features';
import Tibbits from '@appblocks/codegeneration/Tibbits';
import SettingsConfigurations from '@appblocks/codegeneration/SettingsConfigurations';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import Widgets from '@appblocks/codegeneration/Widgets';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import { generateId } from '@appblocks/utils';
import PlatformUtils, { RUNTIMES } from '@appblocks/platforms/PlatformUtils';

const TibbitsData = require('@appblocks/platforms/TibbitDefinitions/tibbits.json');

const tibbitFormFactors: any = {};
TibbitsData.map((tibbit: any): any => {
    tibbitFormFactors[tibbit.good.model] = {
        form: tibbit.main.form.replace(/[ST]/, ''),
        formOriginal: tibbit.main.form,
        category: tibbit.main.category,
        desc_svg: tibbit.good.desc_svg,
        mates: tibbit.main.mates,
        power: tibbit.main.power,
        requires: tibbit.main.requires,
        lines: tibbit.main.lines,
    };
    return null;
});

const getCodeItem = (itemId: string, project: ProjectConfiguration): BaseFeature => {
    let cls;
    if (cls === undefined) {
        cls = Libraries.getItem(itemId, project);
    }
    if (cls === undefined) {
        cls = Peripherals.getItem(itemId, project);
    }
    if (cls === undefined) {
        cls = Features.getItem(itemId, project);
    }
    if (cls === undefined) {
        cls = Tibbits.getItem(itemId, project);
    }
    if (cls === undefined) {
        cls = SettingsConfigurations.getItem(itemId, project);
    }
    if (cls === undefined) {
        cls = Nodes.getItem(itemId, project);
    }
    if (cls === undefined) {
        cls = Widgets.getItem(itemId, project);
    }
    // custom features
    const customFeatures = project.getFeature('custom_features');
    if (customFeatures !== undefined && cls === undefined) {
        const features = customFeatures.getSettingValue('FEATURES');
        for (let i = 0; i < features.length; i += 1) {
            if (features[i].getSettingValue('FEATURE_ID') === itemId) {
                cls = new CustomFeatureBase(itemId);
                break;
            }
        }
    }

    return cls;
};

function getSourceEventNode(project: ProjectConfiguration, node: BaseNode): BaseNode | undefined {
    let eventNode;
    const inputs = node.getInputs();
    if (inputs.length === 0) {
        return node;
    }
    const links = project.getNodeLinks(node, inputs[0].portId);
    for (let i = 0; i < links.length; i++) {
        const linkSource = project.getFeature(links[i].getSettingValue('SOURCE_FEATURE'));
        if (linkSource === undefined) {
            continue;
        }
        const tmp = getSourceEventNode(project, linkSource as BaseNode);
        if (tmp !== undefined) {
            eventNode = tmp;
        }
    }
    return eventNode;
}

export default {

    tibbit_form_factors: tibbitFormFactors,

    getCodeItem,

    getSourceEventNode,

    fromJson(item: any, project: ProjectConfiguration): BaseFeature {
        const itemId = item.id;
        const cls: BaseFeature = getCodeItem(itemId, project);
        if (cls !== undefined) {
            if (cls.id.indexOf('custom_') === 0 || cls.id.indexOf('node_custom') === 0) {
                // TODO determine if this should be done for all features
                cls.initSettings(project);
            }
            cls.project = project;
            if (Array.isArray(item.settings)) {
                for (let i = 0; i < item.settings.length; i += 1) {
                    const settingItem = item.settings[i];
                    for (let k = 0; k < cls.settings.length; k += 1) {
                        if (settingItem.id === cls.settings[k].id) {
                            cls.settings[k].value = settingItem.value;
                            if (cls.settings[k].value === undefined) {
                                cls.settings[k].value = cls.settings[k].default;
                            }
                            if (cls.settings[k].settingType === SettingType.ARRAY) {
                                if (!Array.isArray(cls.settings[k].value)) {
                                    cls.settings[k].value = [];
                                }
                                for (let j = 0; j < cls.settings[k].value.length; j += 1) {
                                    if (cls.settings[k].value[j] != null) {
                                        cls.settings[k].value[j] = this.fromJson(
                                            cls.settings[k].value[j],
                                            project,
                                        );
                                    }
                                }
                            }
                            break;
                        }
                    }
                }
            } else {
                Object.keys(item.settings).forEach((key) => {
                    const settingItem = item.settings[key];
                    for (let k = 0; k < cls.settings.length; k += 1) {
                        if (key === cls.settings[k].id) {
                            cls.settings[k].value = settingItem;
                            if (cls.settings[k].value === undefined) {
                                cls.settings[k].value = cls.settings[k].default;
                            }
                            if (cls.settings[k].settingType === SettingType.ARRAY) {
                                if (!Array.isArray(cls.settings[k].value)) {
                                    cls.settings[k].value = [];
                                }
                                for (let j = 0; j < cls.settings[k].value.length; j += 1) {
                                    if (cls.settings[k].value[j] != null) {
                                        cls.settings[k].value[j] = this.fromJson(
                                            cls.settings[k].value[j],
                                            project,
                                        );
                                    }
                                }
                            }
                            break;
                        }
                    }
                });
            }
        }
        return cls;
    },

    toJson(item: any): JSON {
        const result: any = {
            id: item.id,
        };
        result.settings = {};
        if (Array.isArray(item.settings)) {
            for (let i = 0; i < item.settings.length; i += 1) {
                const settingItem = item.settings[i];
                if (settingItem.settingType === SettingType.ARRAY) {
                    if (settingItem.value === undefined) continue;
                    result.settings[settingItem.id] = [];
                    for (let j = 0; j < settingItem.value.length; j += 1) {
                        result.settings[settingItem.id]
                            .push(this.toJson(settingItem.value[j]));
                    }
                } else {
                    result.settings[settingItem.id] = settingItem.value;
                }
            }
        } else {
            Object.keys(item.settings).forEach((key) => {
                const settingItem = item.settings[key];
                if (settingItem.settingType === SettingType.ARRAY) {
                    if (settingItem.value === undefined) return;
                    result.settings[settingItem.id] = [];
                    for (let j = 0; j < settingItem.value.length; j += 1) {
                        result.settings[settingItem.id]
                            .push(this.toJson(settingItem.value[j]));
                    }
                } else {
                    result.settings[settingItem.id] = settingItem.value;
                }
            });
        }

        return result;
    },

    addFeature(
        project: ProjectConfiguration, feature: any, settingId: string, featureId: string,
    ): void {
        const parsedFeature = this.fromJson(feature, project);
        if (project.getFeature(parsedFeature.getID()) === undefined) {
            if (featureId === undefined) {
                project.addFeature(parsedFeature);
            } else {
                const existingFeature = project.getFeature(featureId);
                if (existingFeature !== undefined) {
                    const arr = existingFeature.getSettingValue(settingId);
                    if (parsedFeature.getSettingValue('ID') === undefined) {
                        parsedFeature.setSettingValue('ID', generateId());
                    }
                    existingFeature.setSettingValue(settingId, [...arr, parsedFeature]);
                }
            }
        }
    },
    removeFeature(project: ProjectConfiguration, featureId: string): void {
        project.removeFeature(featureId);
    },
    updateFeatureSetting(
        project: ProjectConfiguration, featureId: string, settingId: string, value: any,
    ): void {
        const feature = project.getFeature(featureId);
        if (feature !== undefined) {
            if (Array.isArray(value)) {
                const arr = [];
                for (let i = 0; i < value.length; i++) {
                    arr.push(this.fromJson(value[i], project));
                }
                feature.setSettingValue(settingId, arr);
            } else {
                feature.setSettingValue(settingId, value);
            }
        }
    },

    escapeStringTibboBasic(str: string) {
        let result = str;
        result = result.toString().replace(/"/g, '\\x22');
        result = result.toString().replace(/\n/g, '\\n');
        return result;
    },

    escapeString(str: string, runtime: string = 'tios') {
        let result = str;
        if (runtime === 'tios') {
            result = this.escapeStringTibboBasic(result);
        } else {
            result = result.toString().replace(/"/g, '\\"');
            result = result.toString().replace(/\n/g, '\\n');
        }
        return result;
    },

    duplicateFeature(project: ProjectConfiguration, featureId: string): BaseFeature | undefined {
        const feature = project.getFeature(featureId);
        if (!feature) {
            return undefined;
        }
        const json: any = this.toJson(feature);
        const newFeature = this.fromJson({
            ...json,
            settings: {
                ...json.settings,
                ID: generateId(),
            },
        }, project);
        for (let i = 0; i < newFeature.settings.length; i += 1) {
            if (newFeature.settings[i].settingType === SettingType.ARRAY) {
                const arr = newFeature.settings[i].value;
                for (let j = 0; j < arr.length; j += 1) {
                    arr[j] = this.duplicateFeature(project, arr[j].getID());
                }
            }
        }
        return newFeature;
    },

    isSupported(feature: BaseFeature, project: ProjectConfiguration): boolean {
        if (feature.requirements === undefined || feature.requirements.length === 0) {
            return true;
        }
        const runtimes: string[] = Object.values(RUNTIMES);
        const capabilities = PlatformUtils.getCapabilities(project);
        for (let i = 0; i < feature.requirements.length; i += 1) {
            let requirement: string | string[] = feature.requirements[i];
            if (typeof requirement === 'object') {
                // check if is runtime requirement
                for (let j = 0; j < requirement.length; j++) {
                    if (runtimes.includes(requirement[j])) {
                        requirement.push('simulator');
                        break;
                    }
                }
            } else if (typeof requirement === 'string' && runtimes.includes(requirement)) {
                requirement = [requirement, 'simulator'];
            }
            if (typeof requirement === 'string') {
                if (!capabilities.includes(requirement)) {
                    return false;
                }
            } else if (Array.isArray(requirement)) {
                // at least one of the requirements must be met
                let found = false;
                for (let j = 0; j < requirement.length; j += 1) {
                    if (capabilities.includes(requirement[j])) {
                        found = true;
                        break;
                    }
                }
                if (!found) {
                    return false;
                }
            }
        }
        return true;
    },

    getList(): string[] {
        const features: string[] = [];
        features.push(...Libraries.getList());
        features.push(...Peripherals.getList());
        features.push(...Features.getList());
        features.push(...Tibbits.getList());
        features.push(...SettingsConfigurations.getList());
        features.push(...Nodes.getList());
        features.push(...Widgets.getList());
        return features;
    },
};
